import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import ReactMarkdown from "react-markdown";
import { Copy, Check, Calendar } from "lucide-react";
import { apiService } from "../services/apiService";
import {
  canMutateTenantData,
  canAccessClientAdministration,
  CLIENT_ADMIN_PAGE,
  INVOICE_UPLOAD_PREFILL_KEY,
  INVOICE_UPLOAD_RETURN_KEY,
  INVOICE_UPLOAD_SUCCESS_TOAST_KEY,
  INVOICE_ACCOUNT_SEARCH_KEY,
  INVENTORY_ACCOUNT_SEARCH_KEY,
  GLOBAL_SEARCH_INVOICE_HANDOFF_KEY,
  OVERVIEW_INVOICE_STATUS_KEY,
} from "../config/constants";
import { formatCurrency } from "../utils/helpers";
import {
  allocationsFromInvoice,
  areGlAllocationsValid,
  formatGlAllocationsLabel,
  formatGlCodeLabel,
  invoiceGlCodeValues,
  invoiceHasGlCode,
  invoiceMatchesGlCode,
  newGlAllocation,
  serializeGlAllocations,
} from "../utils/glCodes";
import {
  computeMrcChange,
  compareSendToAp,
  compareMonthAmount,
  compareMrcChange,
  comparePriorAmount,
  compareNote,
  formatMonthLongYm,
  formatMonthShortYm,
  getCurrentInvoiceMonth,
  isFutureInvoiceMonth,
  computeComparisonSummaryFromRows,
  computeInvoiceProgressSummary,
  computeLikeForLikeSummary,
  computeComparisonTableTotals,
  formatInvoiceDateForExport,
  invoiceQualifiesForNoteGeneration,
  invoiceNeedsMrcReview,
  getInvoiceDisplayStatus,
  isLargeMrcSwing,
  compareInvoiceGlCode,
  compareInvoiceStatus,
} from "../utils/invoiceMonthDisplay";
import { formatDateYmdStable } from "../utils/dateUtils";
import { SpinnerCustom } from "../components/ui/Spinner";
import Toast from "../components/ui/Toast";
import InvoicesMonthComparisonTable from "../components/invoices/InvoicesMonthComparisonTable";
import InvoicesAllTimeTable from "../components/invoices/InvoicesAllTimeTable";
import InvoicesTablePagination from "../components/invoices/InvoicesTablePagination";
import InvoiceEmailSendStatusModal from "../components/invoices/InvoiceEmailSendStatusModal";
import InvoiceCreditLedgerModal from "../components/invoices/InvoiceCreditLedgerModal";
import ReportingMonthsModal from "../components/invoices/ReportingMonthsModal";
import { noteMarkdownComponents } from "../utils/noteMarkdownComponents";
import CustomSelect from "../components/ui/CustomSelect";

/** Map legacy overview/session status keys to the redesigned status filter ids. */
function normalizeInvoiceStatusFilter(raw) {
  const value = String(raw || "").trim();
  if (!value) return "all";
  if (value === "awaiting") return "expected";
  if (value === "flagged") return "needs_review";
  if (value === "past_due") return "missing";
  if (value === "flagged_or_past_due") return "missing_or_needs_review";
  return value;
}

const InvoicesPage = ({
  userRole,
  tenant,
  tenantId,
  setHeaderOptionsContent,
  onNavigate,
  glCodes = [],
  onAddProviderBillingAccount,
}) => {
  const [providers, setProviders] = useState([]);
  const [billingAccounts, setBillingAccounts] = useState([]);
  const [selectedProvider, setSelectedProvider] = useState("");
  const [selectedBillingAccount, setSelectedBillingAccount] = useState("");
  const [selectedGlCode, setSelectedGlCode] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [viewMode, setViewMode] = useState("focus"); // or "compare"
  const [showCreditColumn, setShowCreditColumn] = useState(false); // "Display Credits" toggle
  const [showInvoiceNumberColumn, setShowInvoiceNumberColumn] = useState(false);
  const [hideClosedAccounts, setHideClosedAccounts] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(() => getCurrentInvoiceMonth());
  const [invoices, setInvoices] = useState([]);
  const [comparison, setComparison] = useState(null);
  const [statusFilter, setStatusFilter] = useState(() => {
    try {
      const fromOverview = String(
        sessionStorage.getItem(OVERVIEW_INVOICE_STATUS_KEY) || "",
      ).trim();
      if (fromOverview) return normalizeInvoiceStatusFilter(fromOverview);
    } catch {
      /* ignore */
    }
    return "all";
  });
  const [invoicesRefreshToken, setInvoicesRefreshToken] = useState(0);
  const [loadingProviders, setLoadingProviders] = useState(true);
  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const [loadingInvoices, setLoadingInvoices] = useState(false);
  const [downloadingId, setDownloadingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [invoicePendingDelete, setInvoicePendingDelete] = useState(null);
  const [invoicePendingClose, setInvoicePendingClose] = useState(null);
  const [closingAccount, setClosingAccount] = useState(false);
  const [editingInvoiceId, setEditingInvoiceId] = useState(null);
  const [editMonthlyMrc, setEditMonthlyMrc] = useState("");
  const [editTotalAmount, setEditTotalAmount] = useState("");
  const [editCredit, setEditCredit] = useState("");
  const [editTaxesAndSurcharges, setEditTaxesAndSurcharges] = useState("");
  const [editDueDate, setEditDueDate] = useState("");
  const [editInvoiceDate, setEditInvoiceDate] = useState("");
  const [editInvoiceNumber, setEditInvoiceNumber] = useState("");
  const [editGlCode, setEditGlCode] = useState("");
  const [editGlSplit, setEditGlSplit] = useState(false);
  const [editGlAllocations, setEditGlAllocations] = useState(() => [
    newGlAllocation("", "100"),
  ]);
  const [editNote, setEditNote] = useState("");
  const [savingInvoiceId, setSavingInvoiceId] = useState(null);
  const [generatingNoteId, setGeneratingNoteId] = useState(null);
  const [noteAutomationSettings, setNoteAutomationSettings] = useState({
    invoiceNoteAutoEnabled: false,
    invoiceMrcChangeThreshold: 5,
  });
  const [error, setError] = useState(null);
  const [toast, setToast] = useState({ message: null, type: "success" });
  const [noteModalInvoice, setNoteModalInvoice] = useState(null);
  const [noteCopied, setNoteCopied] = useState(false);
  const noteContentRef = useRef(null);
  const monthInputRef = useRef(null);
  /** Billing account to re-apply after provider options load (Add-invoice return). */
  const pendingBillingAccountRef = useRef(null);
  const [selectedEmailIds, setSelectedEmailIds] = useState(() => new Set());
  const [sendingEmailId, setSendingEmailId] = useState(null);
  const [emailBatchSending, setEmailBatchSending] = useState(false);
  const [emailSendStatus, setEmailSendStatus] = useState(null);
  const [emailConfirmRequest, setEmailConfirmRequest] = useState(null); // { invoiceId, mode: "send" | "resend" }
  const [showCreditLedgerModal, setShowCreditLedgerModal] = useState(false);
  const [reportingOpen, setReportingOpen] = useState(false);
  const [editVersion, setEditVersion] = useState(null); 
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: "asc",
  });
  const handleSort = (key) => {
    setCurrentPage(1); // 👈 add this
  
    setSortConfig((prev) => {
      if (prev.key === key) {
        return {
          key,
          direction: prev.direction === "asc" ? "desc" : "asc",
        };
      }
      return { key, direction: "asc" };
    });
  };


  const shiftMonth = (ym, delta) => {
    if (!ym || !/^\d{4}-\d{2}$/.test(ym)) return getCurrentInvoiceMonth();
    const [y, m] = ym.split("-").map(Number);
    const d = new Date(y, m - 1 + delta, 1);
    const yy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    return `${yy}-${mm}`;
  };
  
  const handleMonthStep = (delta) => {
    setSelectedMonth((prev) => shiftMonth(prev, delta));
    setStatusFilter("all");
    setCurrentPage(1);
  };

  const openMonthPicker = () => {
    const el = monthInputRef.current;
    if (!el) return;
    if (typeof el.showPicker === "function") {
      try {
        el.showPicker();
        return;
      } catch (_) {
        /* fall through */
      }
    }
    el.focus();
    el.click();
  };

  const isComparisonView = Boolean(selectedMonth && comparison);

  useEffect(() => {
    if (
      !isComparisonView &&
      (statusFilter === "needs_review" ||
        statusFilter === "missing_or_needs_review" ||
        statusFilter === "missing" ||
        statusFilter === "expected" ||
        statusFilter === "received" ||
        statusFilter === "new" ||
        statusFilter === "flagged" ||
        statusFilter === "flagged_or_past_due" ||
        statusFilter === "awaiting" ||
        statusFilter === "past_due")
    ) {
      setStatusFilter("all");
    }
  }, [isComparisonView, statusFilter]);

  const isPresentInvoice = (inv) =>
    inv.row_kind === "present" || (inv.row_kind == null && inv.id != null);

  // Current-month invoice that can be sent to AP (includes MoM "new" rows with an invoice id).
  const isApStatusInvoice = (inv) =>
    isPresentInvoice(inv) || (inv.row_kind === "new" && inv.id != null);

  const glFilterOptions = useMemo(() => {
    const byCode = new Map();
    // Sidebar filter: all tenant GL codes (active and inactive).
    for (const code of Array.isArray(glCodes) ? glCodes : []) {
      const value = String(code.gl_code || "").trim();
      if (!value) continue;
      byCode.set(value.toLowerCase(), {
        gl_code: value,
        description: code.description || "",
      });
    }
    for (const inv of invoices) {
      for (const value of invoiceGlCodeValues(inv)) {
        const key = value.toLowerCase();
        if (!byCode.has(key)) {
          byCode.set(key, { gl_code: value, description: "" });
        }
      }
    }
    return [...byCode.values()].sort((a, b) =>
      String(a.gl_code).localeCompare(String(b.gl_code), undefined, { numeric: true }),
    );
  }, [glCodes, invoices]);

  const glFilteredInvoices = useMemo(() => {
    if (!selectedGlCode) return invoices;
    return invoices.filter((inv) => invoiceMatchesGlCode(inv, selectedGlCode));
  }, [invoices, selectedGlCode]);

  const searchFilteredInvoices = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return glFilteredInvoices;
    return glFilteredInvoices.filter((inv) => {
      const provider = String(inv.provider || "").toLowerCase();
      const account = String(inv.billing_account || "").toLowerCase();
      const invoiceNumber = String(inv.invoice_number || "").toLowerCase();
      return (
        provider.includes(q) ||
        account.includes(q) ||
        invoiceNumber.includes(q)
      );
    });
  }, [glFilteredInvoices, searchQuery]);

  const statusCounts = useMemo(() => {
    let expected = 0;
    let missing = 0;
    let newAccount = 0;
    let needsReview = 0;
    let received = 0;
    let missingGl = 0;
    let credit = 0;
    let notSent = 0;
    let sent = 0;
    let closed = 0;
    for (const inv of searchFilteredInvoices) {
      const displayStatus = getInvoiceDisplayStatus(inv, noteAutomationSettings);
      if (displayStatus === "expected") expected += 1;
      else if (displayStatus === "missing") missing += 1;
      else if (displayStatus === "new") newAccount += 1;
      else if (displayStatus === "needs_review") needsReview += 1;
      else if (displayStatus === "received") received += 1;
      else if (displayStatus === "closed") closed += 1;
      else if (displayStatus === "credit") credit += 1;
      if (!formatGlAllocationsLabel(inv.gl_allocations, inv.gl_code || "")) missingGl += 1;
      if (isApStatusInvoice(inv)) {
        if (inv.email_sent_at) sent += 1;
        else notSent += 1;
      }
    }
    return {
      all: searchFilteredInvoices.length,
      expected,
      missing,
      newAccount,
      needsReview,
      received,
      missingGl,
      credit,
      notSent,
      sent,
      closed,
    };
  }, [searchFilteredInvoices, noteAutomationSettings]);

  const filteredInvoices = useMemo(() => {
    let rows;
    if (statusFilter === "all") {
      rows = searchFilteredInvoices;
    } else if (statusFilter === "missing_or_needs_review") {
      rows = searchFilteredInvoices.filter((inv) => {
        const status = getInvoiceDisplayStatus(inv, noteAutomationSettings);
        return status === "missing" || status === "needs_review";
      });
    } else if (
      statusFilter === "expected" ||
      statusFilter === "missing" ||
      statusFilter === "new" ||
      statusFilter === "needs_review" ||
      statusFilter === "received" ||
      statusFilter === "closed" ||
      statusFilter === "credit"
    ) {
      rows = searchFilteredInvoices.filter(
        (inv) => getInvoiceDisplayStatus(inv, noteAutomationSettings) === statusFilter,
      );
    } else if (statusFilter === "missing_gl") {
      rows = searchFilteredInvoices.filter((inv) => !invoiceHasGlCode(inv));
    } else if (statusFilter === "not_sent") {
      rows = searchFilteredInvoices.filter((inv) => isApStatusInvoice(inv) && !inv.email_sent_at);
    } else if (statusFilter === "sent") {
      rows = searchFilteredInvoices.filter((inv) => isApStatusInvoice(inv) && Boolean(inv.email_sent_at));
    } else {
      rows = searchFilteredInvoices;
    }

    // Hide closed unless the Closed status chip is selected (so that view still works).
    if (hideClosedAccounts && statusFilter !== "closed") {
      rows = rows.filter(
        (inv) =>
          getInvoiceDisplayStatus(inv, noteAutomationSettings) !== "closed",
      );
    }
    return rows;
  }, [
    searchFilteredInvoices,
    statusFilter,
    noteAutomationSettings,
    hideClosedAccounts,
  ]);

  const sortedInvoices = useMemo(() => {
    if (!sortConfig.key) return filteredInvoices;

    const sorted = [...filteredInvoices];
  
    sorted.sort((a, b) => {
      let valA = a[sortConfig.key];
      let valB = b[sortConfig.key];
  
      // Handle null/undefined
      if (valA == null) valA = "";
      if (valB == null) valB = "";
  
      if (sortConfig.key === "email_sent_at") {
        return compareSendToAp(a, b, sortConfig.direction);
      }

      if (sortConfig.key === "mrc_change") {
        return compareMrcChange(a, b, sortConfig.direction);
      }

      if (sortConfig.key === "prior_monthly_mrc") {
        return comparePriorAmount(a, b, sortConfig.direction);
      }

      if (
        sortConfig.key === "monthly_mrc" ||
        sortConfig.key === "total_invoice_amount" ||
        sortConfig.key === "credit_amount"
      ) {
        return compareMonthAmount(
          a,
          b,
          (inv) => inv[sortConfig.key],
          sortConfig.direction,
        );
      }

      if (sortConfig.key === "note") {
        return compareNote(a, b, sortConfig.direction);
      }

      // ✅ DATE (YYYY-MM-DD → string compare works perfectly)
      if (
        sortConfig.key === "invoice_date" ||
        sortConfig.key === "prior_invoice_date"
      ) {
        return sortConfig.direction === "asc"
          ? String(valA).localeCompare(String(valB))
          : String(valB).localeCompare(String(valA));
      }

      if (sortConfig.key === "row_kind") {
        return compareInvoiceStatus(a, b, sortConfig.direction, noteAutomationSettings);
      }

      if (sortConfig.key === "gl_code") {
        return compareInvoiceGlCode(a, b, sortConfig.direction);
      }

      return sortConfig.direction === "asc"
        ? String(valA).localeCompare(String(valB))
        : String(valB).localeCompare(String(valA));
    });
  
    return sorted;
  }, [filteredInvoices, sortConfig, noteAutomationSettings]);

  const filterTotals = useMemo(() => {
    if (!sortedInvoices.length) return null;
    let mrc = 0;
    let total = 0;
    let credit = 0;
    for (const inv of sortedInvoices) {
      if (inv.row_kind === "missing") continue;
      if (inv.credit_amount != null && Number(inv.credit_amount) > 0) {
        credit += Number(inv.credit_amount) || 0;
        continue;
      }
      mrc += Number(inv.monthly_mrc) || 0;
      total += Number(inv.total_invoice_amount) || 0;
    }
    return {
      mrc: Math.round(mrc * 100) / 100,
      total: Math.round(total * 100) / 100,
      credit: Math.round(credit * 100) / 100,
    };
  }, [sortedInvoices]);

  useEffect(() => {
    setSelectedEmailIds(new Set());
  }, [selectedMonth, selectedProvider, selectedBillingAccount, selectedGlCode]);

  const rowsToCsv = (rows) => {
    const headers = [
      "Provider",
      "Billing Account",
      "Invoice Date",
      "Monthly MRC",
      "Total Invoice Amount",
      "Note",
    ];

    const csvRows = rows.map((r) => [
      r.provider,
      `="${r.billing_account}"`,
      formatInvoiceDateForExport(r.invoice_date),
      r.monthly_mrc ?? "",
      r.total_invoice_amount ?? "",
      r.note ?? "",
    ]);

    return [headers, ...csvRows]
      .map((row) =>
        row
          .map((val) => `"${String(val ?? "").replace(/"/g, '""')}"`)
          .join(","),
      )
      .join("\n");
  };  

  const rowsToComparisonCsv = (rows, cmp) => {
    const priorLabel = formatMonthShortYm(cmp.priorMonth);
    const currentLabel = formatMonthShortYm(cmp.invoiceMonth);
  
    const headers = [
      "Provider",
      "Billing Account",
      `${priorLabel} MRC`,
      `${priorLabel} Invoice Date`,
      `${currentLabel} MRC`,
      `${currentLabel} Invoice Date`,
      "Change",
      "Note",
      "Send to AP Date",
    ];
  
    const csvRows = rows.map((r) => {
      const { dollars, percent } = computeMrcChange(
        r.prior_total_invoice_amount,
        r.total_invoice_amount,
      );
  
      let change = "";
      if (r.row_kind === "missing") {
        change = "Expected / Missing";
      } else if (r.row_kind === "new" && r.id == null) {
        change = "Expected";
      } else if (r.row_kind === "new") {
        change = "New invoice";
      } else if (dollars === 0) {
        change = "$0 (0.0%)";
      } else if (dollars > 0) {
        change = `+${formatCurrency(dollars)} (${percent ?? 0}%)`;
      } else {
        change = `-${formatCurrency(Math.abs(dollars))} (${Math.abs(percent ?? 0)}%)`;
      }
  
      return [
        r.provider ?? "",
        r.billing_account ?? "",
        r.prior_monthly_mrc != null ? r.prior_monthly_mrc : "",
        r.prior_invoice_date ? String(r.prior_invoice_date).slice(0, 10) : "",
        r.monthly_mrc != null ? r.monthly_mrc : "",
        r.invoice_date ? String(r.invoice_date).slice(0, 10) : "",
        change,
        r.note ?? "",
        r.email_sent_at ? String(r.email_sent_at).slice(0, 10) : "",
      ];
    });
  
    return [headers, ...csvRows]
      .map((row) =>
        row
          .map((val) => `"${String(val ?? "").replace(/"/g, '""')}"`)
          .join(","),
      )
      .join("\n");
  };

  const exportCsv = () => {
    if (!invoices.length) return;
  
    const allSorted = [...invoices].sort((a, b) => {
      if (!sortConfig.key) return 0;
  
      let valA = a[sortConfig.key];
      let valB = b[sortConfig.key];
  
      if (valA == null) valA = "";
      if (valB == null) valB = "";
  
      if (sortConfig.key === "email_sent_at") {
        return compareSendToAp(a, b, sortConfig.direction);
      }

      if (sortConfig.key === "mrc_change") {
        return compareMrcChange(a, b, sortConfig.direction);
      }

      if (sortConfig.key === "prior_monthly_mrc") {
        return comparePriorAmount(a, b, sortConfig.direction);
      }

      if (
        sortConfig.key === "monthly_mrc" ||
        sortConfig.key === "total_invoice_amount" ||
        sortConfig.key === "credit_amount"
      ) {
        return compareMonthAmount(
          a,
          b,
          (inv) => inv[sortConfig.key],
          sortConfig.direction,
        );
      }

      if (sortConfig.key === "note") {
        return compareNote(a, b, sortConfig.direction);
      }

      if (
        sortConfig.key === "invoice_date" ||
        sortConfig.key === "prior_invoice_date"
      ) {
        return sortConfig.direction === "asc"
          ? String(valA).localeCompare(String(valB))
          : String(valB).localeCompare(String(valA));
      }
  
      if (sortConfig.key === "row_kind") {
        return compareInvoiceStatus(a, b, sortConfig.direction, noteAutomationSettings);
      }

      if (sortConfig.key === "gl_code") {
        return compareInvoiceGlCode(a, b, sortConfig.direction);
      }
  
      return sortConfig.direction === "asc"
        ? String(valA).localeCompare(String(valB))
        : String(valB).localeCompare(String(valA));
    });
  
    const csv = isComparisonView
      ? rowsToComparisonCsv(allSorted, comparison)
      : rowsToCsv(allSorted);
  
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
  
    const a = document.createElement("a");
    a.href = url;
  
    const fileName = selectedMonth
      ? `invoices-${selectedMonth}.csv`
      : "invoices-export.csv";
  
    a.download = fileName;
    a.click();
  
    URL.revokeObjectURL(url);
  };


  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const paginatedInvoices = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return sortedInvoices.slice(start, start + itemsPerPage);
  }, [sortedInvoices, currentPage, itemsPerPage]);

  
  const totalPages = Math.ceil(sortedInvoices.length / itemsPerPage);

  const loadProviders = useCallback(async () => {
    setLoadingProviders(true);
    setError(null);
    try {
      const res = await apiService.getInvoiceProviders();
      if (res?.data) setProviders(res.data);
      else setProviders([]);
    } catch (err) {
      setError(err.message || "Failed to load providers");
      setProviders([]);
    } finally {
      setLoadingProviders(false);
    }
  }, []);

  useEffect(() => {
    loadProviders();
  }, [loadProviders]);

  useEffect(() => {
    try {
      const successToast = sessionStorage.getItem(INVOICE_UPLOAD_SUCCESS_TOAST_KEY);
      if (successToast) {
        sessionStorage.removeItem(INVOICE_UPLOAD_SUCCESS_TOAST_KEY);
        setToast({ message: successToast, type: "success" });
      }

      const returnRaw = sessionStorage.getItem(INVOICE_UPLOAD_RETURN_KEY);
      if (returnRaw) {
        const ret = JSON.parse(returnRaw);
        sessionStorage.removeItem(INVOICE_UPLOAD_RETURN_KEY);
        if (ret.invoiceMonth && /^\d{4}-\d{2}$/.test(ret.invoiceMonth)) {
          setSelectedMonth(ret.invoiceMonth);
        }
        // Restore the filters the user had before "Add invoice".
        // Billing account is re-applied after provider account options load.
        const restoredProvider =
          ret.provider != null ? String(ret.provider).trim() : "";
        const restoredAccount =
          ret.billingAccount != null
            ? String(ret.billingAccount).trim()
            : "";
        pendingBillingAccountRef.current = restoredAccount || null;
        if (restoredProvider) {
          setSelectedProvider(restoredProvider);
        } else {
          pendingBillingAccountRef.current = null;
        }
        if (ret.glCode != null && String(ret.glCode).trim() !== "") {
          setSelectedGlCode(String(ret.glCode).trim());
        }
        if (ret.statusFilter != null && String(ret.statusFilter).trim() !== "") {
          setStatusFilter(String(ret.statusFilter).trim());
        } else {
          setStatusFilter("all");
        }
        if (ret.searchQuery != null) {
          setSearchQuery(String(ret.searchQuery));
        }
        if (typeof ret.hideClosedAccounts === "boolean") {
          setHideClosedAccounts(ret.hideClosedAccounts);
        }
        setCurrentPage(1);
        setInvoicesRefreshToken((t) => t + 1);
        return;
      }
    } catch {
      sessionStorage.removeItem(INVOICE_UPLOAD_RETURN_KEY);
      sessionStorage.removeItem(INVOICE_UPLOAD_SUCCESS_TOAST_KEY);
    }
    if (!selectedMonth || !/^\d{4}-\d{2}$/.test(selectedMonth)) {
      setSelectedMonth(getCurrentInvoiceMonth());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run once on mount
  }, []);

  useEffect(() => {
    try {
      const globalHandoffRaw = sessionStorage.getItem(
        GLOBAL_SEARCH_INVOICE_HANDOFF_KEY,
      );
      if (globalHandoffRaw) {
        sessionStorage.removeItem(GLOBAL_SEARCH_INVOICE_HANDOFF_KEY);
        const handoff = JSON.parse(globalHandoffRaw);
        const search = String(handoff?.search || "").trim();
        const month = String(handoff?.month || "").trim();
        if (search) setSearchQuery(search);
        if (month && /^\d{4}-\d{2}$/.test(month)) setSelectedMonth(month);
        setCurrentPage(1);
      } else {
        const accountSearch = sessionStorage.getItem(INVOICE_ACCOUNT_SEARCH_KEY);
        if (accountSearch) {
          sessionStorage.removeItem(INVOICE_ACCOUNT_SEARCH_KEY);
          setSearchQuery(accountSearch);
          setCurrentPage(1);
        }
      }
      const overviewStatus = String(
        sessionStorage.getItem(OVERVIEW_INVOICE_STATUS_KEY) || "",
      ).trim();
      if (overviewStatus) {
        sessionStorage.removeItem(OVERVIEW_INVOICE_STATUS_KEY);
        setStatusFilter(normalizeInvoiceStatusFilter(overviewStatus));
        setCurrentPage(1);
      }
    } catch {
      sessionStorage.removeItem(INVOICE_ACCOUNT_SEARCH_KEY);
      sessionStorage.removeItem(GLOBAL_SEARCH_INVOICE_HANDOFF_KEY);
      sessionStorage.removeItem(OVERVIEW_INVOICE_STATUS_KEY);
    }
  }, []);

  useEffect(() => {
    if (!selectedProvider) {
      setBillingAccounts([]);
      setSelectedBillingAccount("");
      pendingBillingAccountRef.current = null;
      return;
    }
    let cancelled = false;
    setLoadingAccounts(true);
    const pendingAccount = pendingBillingAccountRef.current;
    if (!pendingAccount) {
      setSelectedBillingAccount("");
    }
    apiService
      .getInvoiceBillingAccounts(selectedProvider)
      .then((res) => {
        if (cancelled) return;
        const accounts = Array.isArray(res?.data) ? res.data : [];
        setBillingAccounts(accounts);
        if (pendingAccount) {
          pendingBillingAccountRef.current = null;
          const match = accounts.some((acc) => {
            const value = typeof acc === "string" ? acc : acc?.billing_account;
            return String(value || "") === pendingAccount;
          });
          setSelectedBillingAccount(match ? pendingAccount : "");
        }
      })
      .catch(() => {
        if (!cancelled) {
          setBillingAccounts([]);
          if (pendingAccount) {
            pendingBillingAccountRef.current = null;
            setSelectedBillingAccount("");
          }
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingAccounts(false);
      });
    return () => { cancelled = true; };
  }, [selectedProvider]);

  const monthTotals = useMemo(() => {
    if (!selectedMonth || invoices.length === 0) return null;
    let mrc = 0;
    let total = 0;
    for (const inv of invoices) {
      if (inv.credit_amount != null && Number(inv.credit_amount) > 0) continue;
      mrc += Number(inv.monthly_mrc) || 0;
      total += Number(inv.total_invoice_amount) || 0;
    }
    return { count: invoices.length, mrc, total };
  }, [selectedMonth, invoices]);

  useEffect(() => {
    let cancelled = false;
    setLoadingInvoices(true);
    setError(null);
    const req = selectedMonth
      ? apiService.listInvoices({
          invoiceMonth: selectedMonth,
          ...(selectedProvider ? { provider: selectedProvider } : {}),
          ...(selectedBillingAccount ? { billingAccount: selectedBillingAccount } : {}),
        })
      : apiService.listInvoices({
          ...(selectedProvider ? { provider: selectedProvider } : {}),
          ...(selectedBillingAccount ? { billingAccount: selectedBillingAccount } : {}),
        });

    req
      .then((res) => {
        if (cancelled) return;
        if (res?.data) setInvoices(res.data);
        else setInvoices([]);
        setComparison(selectedMonth && res?.comparison ? res.comparison : null);
        if (!selectedMonth) setStatusFilter("all");
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err.message || "Failed to load invoices");
          setInvoices([]);
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingInvoices(false);
      });
    return () => { cancelled = true; };
  }, [selectedMonth, selectedProvider, selectedBillingAccount, invoicesRefreshToken]);

  useEffect(() => {
    let cancelled = false;
    apiService
      .getInvoiceNoteAutomationSettings()
      .then((res) => {
        if (cancelled || !res?.data) return;
        setNoteAutomationSettings({
          invoiceNoteAutoEnabled: Boolean(res.data.invoiceNoteAutoEnabled),
          invoiceMrcChangeThreshold:
            res.data.invoiceMrcChangeThreshold != null
              ? Number(res.data.invoiceMrcChangeThreshold)
              : 5,
        });
      })
      .catch(() => {
        if (!cancelled) {
          setNoteAutomationSettings({
            invoiceNoteAutoEnabled: false,
            invoiceMrcChangeThreshold: 5,
          });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [tenantId, invoicesRefreshToken]);

  const canShowGenerateNote = useCallback(
    (inv) => invoiceQualifiesForNoteGeneration(inv, noteAutomationSettings),
    [noteAutomationSettings],
  );

  // const formatMonthLabel = (ym) => {
  //   if (!ym || !/^\d{4}-\d{2}$/.test(ym)) return ym;
  //   const [y, m] = ym.split("-").map(Number);
  //   return new Date(y, m - 1, 1).toLocaleDateString(undefined, {
  //     month: "long",
  //     year: "numeric",
  //   });
  // };

  const formatMonthLabel = formatMonthLongYm;

  const canDeleteInvoice = canMutateTenantData(userRole);
  const canEditInvoice = canMutateTenantData(userRole);
  const canSendEmail = canMutateTenantData(userRole);
  const canCloseAccount = canMutateTenantData(userRole);
  const canAddInvoice =
    canAccessClientAdministration(userRole) && typeof onNavigate === "function";

  const formatEmailSentDate = (value) => {
    if (!value) return null;
    return formatDateYmdStable(value) || null;
  };

  const applyEmailSendResults = (results) => {
    if (!Array.isArray(results) || results.length === 0) return;
    const sentById = new Map();
    for (const r of results) {
      if (r.success && r.invoiceId != null) {
        sentById.set(r.invoiceId, r.emailSentAt || new Date().toISOString());
      }
    }
    if (sentById.size === 0) return;
    setInvoices((prev) =>
      prev.map((inv) => {
        const sentAt = sentById.get(inv.id);
        return sentAt ? { ...inv, email_sent_at: sentAt } : inv;
      }),
    );
  };

  const handleToggleEmailSelect = (invoiceId) => {
    setSelectedEmailIds((prev) => {
      const next = new Set(prev);
      if (next.has(invoiceId)) next.delete(invoiceId);
      else next.add(invoiceId);
      return next;
    });
  };

  const invoiceById = useMemo(() => {
    const map = new Map();
    for (const inv of invoices) {
      if (inv.id != null) map.set(inv.id, inv);
    }
    return map;
  }, [invoices]);

  const runInvoiceEmailSend = useCallback(
    async (ids, { clearSelection = true } = {}) => {
      if (!ids.length) return;

      setEmailBatchSending(true);
      if (ids.length === 1) {
        setSendingEmailId(ids[0]);
      }
      setError(null);
      setEmailSendStatus({
        open: true,
        phase: "sending",
        total: ids.length,
        processed: 0,
        sentCount: 0,
        failed: [],
      });

      const results = [];
      const failedRows = [];

      for (let i = 0; i < ids.length; i += 1) {
        const id = ids[i];
        const inv = invoiceById.get(id);
        try {
          const res = await apiService.sendInvoiceEmail(id);
          if (res?.success) {
            results.push({
              success: true,
              invoiceId: id,
              emailSentAt: res.data?.emailSentAt,
            });
          } else {
            const reason = res?.message || "Failed to send email";
            failedRows.push({
              provider: inv?.provider ?? "—",
              billing_account: inv?.billing_account ?? "—",
              invoice_date: formatInvoiceDateForExport(inv?.invoice_date),
              reason,
            });
            results.push({ success: false, invoiceId: id, message: reason });
          }
        } catch (err) {
          const reason = err.message || "Failed to send email";
          failedRows.push({
            provider: inv?.provider ?? "—",
            billing_account: inv?.billing_account ?? "—",
            invoice_date: formatInvoiceDateForExport(inv?.invoice_date),
            reason,
          });
          results.push({ success: false, invoiceId: id, message: reason });
        }

        const sentSoFar = results.filter((r) => r.success).length;
        setEmailSendStatus({
          open: true,
          phase: "sending",
          total: ids.length,
          processed: i + 1,
          sentCount: sentSoFar,
          failed: [...failedRows],
        });
      }

      applyEmailSendResults(results);

      if (clearSelection) {
        setSelectedEmailIds((prev) => {
          const next = new Set(prev);
          for (const r of results) {
            if (r.success && r.invoiceId != null) next.delete(r.invoiceId);
          }
          return next;
        });
      }

      const sentCount = results.filter((r) => r.success).length;
      setEmailSendStatus({
        open: true,
        phase: "done",
        total: ids.length,
        processed: ids.length,
        sentCount,
        failed: failedRows,
      });
      setEmailBatchSending(false);
      setSendingEmailId(null);
    },
    [invoiceById],
  );

  const beginInvoiceEmailSend = useCallback(
    (ids, { clearSelection = true } = {}) => {
      if (!ids.length) return;

      const missingGl = ids
        .map((id) => invoiceById.get(id))
        .filter((inv) => inv && !invoiceHasGlCode(inv))
        .map((inv) => ({
          provider: inv.provider ?? "—",
          billing_account: inv.billing_account ?? "—",
          invoice_date: formatInvoiceDateForExport(inv.invoice_date),
        }));

      if (missingGl.length > 0) {
        setEmailSendStatus({
          open: true,
          phase: "missing_gl",
          total: ids.length,
          missingGl,
          pendingIds: ids,
          clearSelection,
        });
        return;
      }

      runInvoiceEmailSend(ids, { clearSelection });
    },
    [invoiceById, runInvoiceEmailSend],
  );

  const handleConfirmMissingGlSend = useCallback(() => {
    const { pendingIds, clearSelection } = emailSendStatus || {};
    if (!pendingIds?.length) {
      setEmailSendStatus(null);
      return;
    }
    runInvoiceEmailSend(pendingIds, { clearSelection: clearSelection ?? true });
  }, [emailSendStatus, runInvoiceEmailSend]);

  const handleSendSelectedEmails = async () => {
    const ids = [...selectedEmailIds];
    if (ids.length === 0) {
      setToast({ message: "Select at least one invoice to email.", type: "error" });
      return;
    }
    beginInvoiceEmailSend(ids, { clearSelection: true });
  };

  const handleResendEmail = async (invoiceId) => {
    beginInvoiceEmailSend([invoiceId], { clearSelection: false });
  };

  const handleViewCircuitsOnAccount = useCallback(
    (billingAccount) => {
      const account = String(billingAccount || "").trim();
      if (!account || typeof onNavigate !== "function") return;
      try {
        sessionStorage.setItem(INVENTORY_ACCOUNT_SEARCH_KEY, account);
      } catch {
        /* ignore */
      }
      onNavigate("inventory");
    },
    [onNavigate],
  );

  // Instead of sending immediately, Send/Resend row actions now open a
  // confirmation modal. The actual send only happens once the user
  // confirms (see handleConfirmEmailSend below), to avoid accidental sends.
  const requestResendEmail = (invoiceId) => {
    setEmailConfirmRequest({ invoiceId, mode: "resend" });
  };

  const handleAddMissingInvoice = (inv) => {
    if (!selectedMonth || !canAddInvoice) return;
    sessionStorage.setItem(
      INVOICE_UPLOAD_PREFILL_KEY,
      JSON.stringify({
        provider: inv.provider,
        billingAccount: inv.billing_account,
        glCode: formatGlAllocationsLabel(inv.gl_allocations, inv.gl_code || ""),
        invoiceMonth: selectedMonth,
      }),
    );
    sessionStorage.setItem(
      INVOICE_UPLOAD_RETURN_KEY,
      JSON.stringify({
        returnTo: "invoices",
        invoiceMonth: selectedMonth,
        provider: selectedProvider || "",
        billingAccount: selectedBillingAccount || "",
        glCode: selectedGlCode || "",
        statusFilter: statusFilter || "all",
        searchQuery: searchQuery || "",
        hideClosedAccounts: Boolean(hideClosedAccounts),
      }),
    );
    onNavigate(CLIENT_ADMIN_PAGE.INVOICE);
  };

  const comparisonSummary = useMemo(() => {
    if (!isComparisonView || !comparison?.summary) return null;
    return computeComparisonSummaryFromRows(
      invoices,
      comparison.summary.priorTotal,
    );
  }, [isComparisonView, comparison, invoices]);

  const progressSummary = useMemo(() => {
    if (!isComparisonView) return null;
    return computeInvoiceProgressSummary(invoices);
  }, [isComparisonView, invoices]);

  const likeForLikeSummary = useMemo(() => {
    if (!isComparisonView) return null;
    return computeLikeForLikeSummary(invoices);
  }, [isComparisonView, invoices]);

  const comparisonTableTotals = useMemo(() => {
    if (!isComparisonView) return null;
    return computeComparisonTableTotals(sortedInvoices);
  }, [isComparisonView, sortedInvoices]);

  const refreshInvoices = () => {
    setInvoicesRefreshToken((t) => t + 1);
    loadProviders();
  };

  const renderChangeCell = (inv) => {
    if (inv.row_kind === "missing") {
      return <span className="text-stone-400">—</span>;
    }
    if (inv.row_kind === "new" && inv.id == null) {
      return <span className="text-stone-400">—</span>;
    }
    if (inv.row_kind === "new") {
      return <span className="text-stone-400">—</span>;
    }
    const { dollars, percent } = computeMrcChange(
      inv.prior_total_invoice_amount,
      inv.total_invoice_amount,
    );
    const large = isLargeMrcSwing(dollars, percent);
    const weight = large ? "font-bold" : "font-medium";
    if (dollars === 0) {
      return (
        <span className="inline-block max-w-full text-sm leading-snug text-center text-stone-500 break-words">
          $0 (0.0%)
        </span>
      );
    }
    if (dollars > 0) {
      return (
        <span className={`inline-block max-w-full text-sm leading-snug text-center break-words text-rose-600 tabular-nums ${weight}`}>
          ↑ {formatCurrency(dollars)}
          {percent != null ? ` (${percent}%)` : ""}
        </span>
      );
    }
    return (
      <span className={`inline-block max-w-full text-sm leading-snug text-center break-words text-emerald-700 tabular-nums ${weight}`}>
        ↓ {formatCurrency(Math.abs(dollars))}
        {percent != null ? ` (${Math.abs(percent)}%)` : ""}
      </span>
    );
  };

  const renderNoteCell = (inv) => {
    if (inv.row_kind === "missing") {
      return <span className="text-stone-400">—</span>;
    }
    const text = inv.note != null ? String(inv.note).trim() : "";
    if (!text) {
      return <span className="text-stone-400">—</span>;
    }
    return (
      <button
        type="button"
        onClick={() => setNoteModalInvoice(inv)}
        className="inline-flex items-center justify-center rounded-md p-0.5 text-base leading-none hover:bg-stone-100 focus:outline-none focus:ring-2 focus:ring-sky-400 focus:ring-offset-1"
        title="View note"
        aria-label="View note"
      >
        💬
      </button>
    );
  };

  const handleSendSingleEmail = async (invoiceId) => {
    beginInvoiceEmailSend([invoiceId], { clearSelection: false });
  };

  // Same pattern as requestResendEmail above: open the confirmation modal
  // rather than sending right away.
  const requestSendEmail = (invoiceId) => {
    setEmailConfirmRequest({ invoiceId, mode: "send" });
  };

  const handleCancelEmailConfirm = () => {
    if (emailBatchSending) return;
    setEmailConfirmRequest(null);
  };

  const handleConfirmEmailSend = () => {
    if (!emailConfirmRequest) return;
    const { invoiceId, mode } = emailConfirmRequest;
    setEmailConfirmRequest(null);
    if (mode === "resend") {
      handleResendEmail(invoiceId);
    } else {
      handleSendSingleEmail(invoiceId);
    }
  };

  const handleToggleSelectAllOnPage = (pageIds) => {
    setSelectedEmailIds((prev) => {
      const next = new Set(prev);
      const allSelected = pageIds.length > 0 && pageIds.every((id) => next.has(id));
      if (allSelected) {
        for (const id of pageIds) next.delete(id);
      } else {
        for (const id of pageIds) next.add(id);
      }
      return next;
    });
  };

  const currentMonthShort = selectedMonth
    ? formatMonthShortYm(selectedMonth).split(" ")[0]
    : "";
  const priorMonthShort =
    comparison?.priorMonth
      ? formatMonthShortYm(comparison.priorMonth).split(" ")[0]
      : "";
      const STATUS_FILTER_STYLES = {
        all:        { dot: "bg-stone-400",  active: "bg-stone-900 border-stone-900 text-white" },
        expected:   { dot: "bg-stone-400",  active: "bg-stone-900 border-stone-900 text-white" },
        missing:    { dot: "bg-red-500",    active: "bg-stone-900 border-stone-900 text-white" },
        new:        { dot: "bg-amber-500",  active: "bg-stone-900 border-stone-900 text-white" },
        needs_review: { dot: "bg-rose-500", active: "bg-stone-900 border-stone-900 text-white" },
        missing_or_needs_review: { dot: "bg-rose-500", active: "bg-stone-900 border-stone-900 text-white" },
        received:   { dot: "bg-emerald-500",active: "bg-stone-900 border-stone-900 text-white" },
        missing_gl: { dot: "bg-red-500",    active: "bg-stone-900 border-stone-900 text-white" },
        credit:     { dot: "bg-sky-500",    active: "bg-stone-900 border-stone-900 text-white" },
        not_sent:   { dot: "bg-amber-500",  active: "bg-stone-900 border-stone-900 text-white" },
        sent:       { dot: "bg-emerald-500",active: "bg-stone-900 border-stone-900 text-white" },
        closed:     { dot: "bg-stone-400",  active: "bg-stone-900 border-stone-900 text-white" },
      };
      
        const statusFilterOptions = [
          { id: "all", label: "All", count: statusCounts.all },
          ...(isComparisonView
            ? [
                { id: "expected", label: "Expected", count: statusCounts.expected },
                { id: "missing", label: "Missing", count: statusCounts.missing },
                { id: "needs_review", label: "Needs review", count: statusCounts.needsReview },
                { id: "received", label: "Received", count: statusCounts.received },
                { id: "new", label: "New", count: statusCounts.newAccount },
              ]
            : []),
          ...(statusFilter === "missing_or_needs_review"
            ? [
                {
                  id: "missing_or_needs_review",
                  label: "Missing or needs review",
                  count:
                    (statusCounts.missing || 0) + (statusCounts.needsReview || 0),
                },
              ]
            : []),
          { id: "missing_gl", label: "Missing GL", count: statusCounts.missingGl },
          { id: "credit", label: "Credit", count: statusCounts.credit },
          { id: "closed", label: "Closed", count: statusCounts.closed },
          { id: "not_sent", label: "AP Unsent", count: statusCounts.notSent },
          { id: "sent", label: "AP Sent", count: statusCounts.sent },
        ];
        
  const AP_STATUS_IDS = new Set(["not_sent", "sent"]);

  const apStatusFilterOptions = [
    { id: "all", label: "All" },
    { id: "not_sent", label: "AP Unsent" },
    { id: "sent", label: "AP Sent" },
  ];

  const apStatusSelectValue = AP_STATUS_IDS.has(statusFilter)
    ? statusFilter
    : "all";

  const handleClearAllFilters = () => {
    setSearchQuery("");
    setSelectedProvider("");
    setSelectedBillingAccount("");
    setSelectedGlCode("");
    setStatusFilter("all");
    setHideClosedAccounts(false);
    setCurrentPage(1);
  };

  const renderNoteLink = (inv) => {
    const text = inv.note != null ? String(inv.note).trim() : "";
    if (!text) {
      return <span className="text-gray-400">—</span>;
    }
    return (
      <button
        type="button"
        onClick={() => setNoteModalInvoice(inv)}
        className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-600 hover:text-slate-800 hover:underline focus:outline-none focus:ring-2 focus:ring-slate-500 focus:ring-offset-1 rounded"
      >
        <svg
          className="w-3.5 h-3.5 flex-shrink-0 text-slate-600"
          viewBox="0 0 20 20"
          fill="currentColor"
          aria-hidden="true"
        >
          <path d="M4 4a2 2 0 012-2h4.586a1 1 0 01.707.293l4.414 4.414a1 1 0 01.293.707V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" />
        </svg>
        View note
      </button>
    );
  };

  const renderStatusNoteCell = (inv) => {
    const badges = [];
    if (inv.row_kind === "missing") {
      badges.push(
        <span
          key="missing"
          className="inline-block px-2 py-0.5 text-xs font-semibold rounded bg-red-100 text-red-800"
        >
          Missing
        </span>,
      );
    } else if (inv.row_kind === "new") {
      badges.push(
        <span
          key="new"
          className="inline-block px-2 py-0.5 text-xs font-semibold rounded bg-emerald-100 text-emerald-800"
        >
          New invoice
        </span>,
      );
    }

    if (inv.row_kind === "missing") {
      return <div className="flex flex-wrap gap-1">{badges}</div>;
    }

    const hasNote = inv.note != null && String(inv.note).trim();
    if (!badges.length && !hasNote) {
      return <span className="text-gray-400">—</span>;
    }

    return (
      <div className="flex flex-col gap-1.5 items-start">
        {badges.length > 0 && <div className="flex flex-wrap gap-1">{badges}</div>}
        {hasNote && renderNoteLink(inv)}
      </div>
    );
  };

  const invoiceDateToInputValue = (d) => {
    if (!d) return "";
    if (typeof d === "string") return d.slice(0, 10);
    if (d instanceof Date && !Number.isNaN(d.getTime())) {
      return d.toISOString().slice(0, 10);
    }
    return String(d).slice(0, 10);
  };

  const beginEditInvoice = (inv) => {
    setError(null);
    setEditingInvoiceId(inv.id);
    setEditInvoiceDate(invoiceDateToInputValue(inv.invoice_date));
    setEditInvoiceNumber(inv.invoice_number != null ? String(inv.invoice_number) : "");
    setEditGlCode(inv.gl_code != null ? String(inv.gl_code) : "");
    const nextAllocations = allocationsFromInvoice(inv);
    setEditGlAllocations(nextAllocations);
    setEditGlSplit(nextAllocations.length > 1);
    setEditNote(inv.note != null ? String(inv.note) : "");
    setEditMonthlyMrc(
      inv.monthly_mrc != null && inv.monthly_mrc !== "" ? String(inv.monthly_mrc) : "",
    );
    setEditTotalAmount(
      inv.total_invoice_amount != null && inv.total_invoice_amount !== ""
        ? String(inv.total_invoice_amount)
        : "",
    );
    setEditCredit(
      inv.credit_amount != null && inv.credit_amount !== ""
        ? String(inv.credit_amount)
        : "",
    );
    setEditTaxesAndSurcharges(
      inv.taxes_and_surcharges != null && inv.taxes_and_surcharges !== ""
        ? String(inv.taxes_and_surcharges)
        : "",
    );
    setEditDueDate(invoiceDateToInputValue(inv.due_date));
    setEditVersion(inv.version ?? null);
  };

  const cancelEditInvoice = () => {
    if (savingInvoiceId || generatingNoteId) return;
    setEditingInvoiceId(null);
    setEditMonthlyMrc("");
    setEditTotalAmount("");
    setEditCredit("");
    setEditTaxesAndSurcharges("");
    setEditDueDate("");
    setEditInvoiceDate("");
    setEditInvoiceNumber("");
    setEditGlCode("");
    setEditGlSplit(false);
    setEditGlAllocations([newGlAllocation("", "100")]);
    setEditNote("");
    setEditVersion(null);
  };

  const handleGenerateNote = async (invId) => {
    setGeneratingNoteId(invId);
    setError(null);
    try {
      const res = await apiService.generateInvoiceNote(invId);
      const note = res?.data?.note;
      if (note != null) {
        setEditNote(String(note));
        setToast({
          message: "Note generated. Review and save when ready.",
          type: "success",
        });
      }
    } catch (err) {
      setError(err.message || "Failed to generate note");
      setToast({ message: err.message || "Failed to generate note", type: "error" });
    } finally {
      setGeneratingNoteId(null);
    }
  };

  const handleMarkReviewed = async (inv) => {
    if (!inv?.id) return;
    setError(null);
    try {
      const res = await apiService.markInvoiceReviewed(inv.id);
      const reviewedAt = res?.data?.mrc_reviewed_at || new Date().toISOString();
      const nextVersion =
        res?.data?.version != null ? res.data.version : inv.version;
      setInvoices((prev) =>
        prev.map((row) =>
          row.id === inv.id
            ? {
                ...row,
                mrc_reviewed_at: reviewedAt,
                mrc_reviewed_by: res?.data?.mrc_reviewed_by ?? row.mrc_reviewed_by,
                version: nextVersion,
              }
            : row,
        ),
      );
      setToast({ message: "Invoice marked as reviewed", type: "success" });
    } catch (err) {
      setError(err.message || "Failed to mark invoice reviewed");
      setToast({
        message: err.message || "Failed to mark invoice reviewed",
        type: "error",
      });
    }
  };

  const canShowMarkReviewed = useCallback(
    (inv) =>
      Boolean(
        canEditInvoice &&
          inv?.id != null &&
          invoiceNeedsMrcReview(inv, noteAutomationSettings),
      ),
    [canEditInvoice, noteAutomationSettings],
  );

  const handleSaveInvoiceEdit = async (invId) => {
    if (!String(editInvoiceNumber || "").trim()) {
      setError("Invoice number is required");
      setToast({ message: "Invoice number is required", type: "error" });
      return;
    }
    if (isFutureInvoiceMonth(editInvoiceDate)) {
      setError("Invoice date cannot be in a future month");
      setToast({
        message: "Invoice date cannot be in a future month",
        type: "error",
      });
      return;
    }
    if (!areGlAllocationsValid(editGlAllocations)) {
      setError("GL shares must add up to 100% and each line needs a GL code");
      setToast({
        message: "GL shares must add up to 100% and each line needs a GL code",
        type: "error",
      });
      return;
    }
    setSavingInvoiceId(invId);
    setError(null);
    try {
      const res = await apiService.updateInvoiceAmounts(invId, {
        monthly_mrc: editMonthlyMrc,
        total_invoice_amount: editTotalAmount,
        credit_amount: editCredit.trim() ? editCredit : null,
        taxes_and_surcharges: editTaxesAndSurcharges.trim()
          ? editTaxesAndSurcharges
          : null,
        due_date: editDueDate || null,
        invoice_date: editInvoiceDate,
        invoice_number: editInvoiceNumber.trim(),
        gl_code: String(editGlAllocations[0]?.gl_code || "").trim(),
        gl_allocations: serializeGlAllocations(editGlAllocations),
        note: editNote,
        version: editVersion,
      });
      const updated = res?.data;
      if (updated) {
        setInvoices((prev) =>
          prev.map((row) => (row.id === invId ? { ...row, ...updated } : row)),
        );
      }
      setEditingInvoiceId(null);
      setEditMonthlyMrc("");
      setEditTotalAmount("");
      setEditCredit("");
      setEditTaxesAndSurcharges("");
      setEditDueDate("");
      setEditInvoiceDate("");
      setEditInvoiceNumber("");
      setEditGlCode("");
      setEditGlSplit(false);
      setEditGlAllocations([newGlAllocation("", "100")]);
      setEditNote("");
      setEditVersion(null);
      setToast({ message: "Invoice updated successfully", type: "success" });
    } catch (err) {
      if (err.status === 409) {
        // Someone else edited this invoice since it was loaded. Refresh the
        // list so the row picks up the current data + version, and let the
        // user know rather than silently discarding their edit.
        setError("This invoice was updated by someone else. The row has been refreshed. Please reapply your changes.");
        setToast({
          title: "Changes Not Saved",
          message: "Someone else updated this invoice. We've loaded the latest version. \nPlease review and save your changes again.",
          type: "warning",
        });
        setInvoicesRefreshToken((t) => t + 1);
        setEditingInvoiceId(null);
      } else if (err.status === 404) {
        setError("This invoice was not found. It may have been deleted by someone else.");
        setToast({
          title: "Invoice Not Found",
          message: "This invoice was deleted by another user. The list has been refreshed.",
          type: "warning",
        });
        setInvoicesRefreshToken((t) => t + 1);
        setEditingInvoiceId(null);
      } else {
        setError(err.message || "Update failed");
        setToast({
          title: "Update Failed",
          message: "This invoice was updated by another user and could not be saved. \nThe latest data has been loaded. Please try again if needed.",
          type: "error",
        });
      }
    } finally {
      setSavingInvoiceId(null);
    }
  };

  const formatInvoiceDate = (d) => {
    if (!d) return "—";
    return typeof d === "string" ? d.slice(0, 10) : d;
  };

  const handleDownload = async (id) => {
    setDownloadingId(id);
    try {
      await apiService.downloadInvoice(id);
    } catch (err) {
      setError(err.message || "Download failed");
    } finally {
      setDownloadingId(null);
    }
  };

  const handleOpenDeleteModal = (inv) => {
    setError(null);
    setInvoicePendingDelete(inv);
    setToast({ message: null, type: "success" });
  };

  const handleCancelDeleteInvoice = () => {
    if (deletingId) return;
    setInvoicePendingDelete(null);
  };

  const handleOpenCloseModal = (inv) => {
    setError(null);
    setInvoicePendingClose(inv);
    setToast({ message: null, type: "success" });
  };

  const handleCancelCloseAccount = () => {
    if (closingAccount) return;
    setInvoicePendingClose(null);
  };

  const handleConfirmCloseAccount = async () => {
    const inv = invoicePendingClose;
    if (!inv || closingAccount) return;
    setClosingAccount(true);
    setError(null);
    try {
      await apiService.closeInvoiceBillingAccount(inv.provider, inv.billing_account);
      setInvoicePendingClose(null);
      setToast({
        message: "Account closed. It will stay in this month and drop from future months.",
        type: "success",
      });
      setInvoicesRefreshToken((t) => t + 1);
    } catch (err) {
      setError(err.message || "Failed to close account");
      setToast({
        title: "Close account failed",
        message: err.message || "Failed to close account",
        type: "error",
      });
    } finally {
      setClosingAccount(false);
    }
  };

  const handleConfirmDeleteInvoice = async () => {
    const inv = invoicePendingDelete;
    if (!inv) return;
    setDeletingId(inv.id);
    setError(null);
    try {
      await apiService.deleteInvoice(inv.id, inv.version);
      setInvoices((prev) => prev.filter((row) => row.id !== inv.id));
      setInvoicePendingDelete(null);
      setToast({ message: "Invoice deleted successfully", type: "success" });
    } catch (err) {
      if (err.status === 409) {
        // Someone edited or deleted this invoice since it was loaded.
        // Refresh the list so the row reflects reality, and let the user
        // know instead of leaving them staring at a stale row.
        setError("This invoice was changed by someone else. The list has been refreshed.");
        setToast({
          title: "Delete Failed",
          message:"This invoice was updated by another user and could not be deleted. \nThe latest data has been loaded. Please try deleting it again if needed.",
          type: "warning",
        });
        setInvoicePendingDelete(null);
        setInvoicesRefreshToken((t) => t + 1);
      } else if (err.status === 404) {
        // Already deleted by someone else — just drop it from the list.
        setInvoices((prev) => prev.filter((row) => row.id !== inv.id));
        setInvoicePendingDelete(null);
        setToast({ title: "Invoice Not Found", message: "Invoice was already deleted by another user. The list has been refreshed.", type: "error" });
      } else {
        setError(err.message || "Delete failed");
        setToast({ title: "Delete Failed", message: err.message || "Delete failed", type: "error" });
      }
    } finally {
      setDeletingId(null);
    }
  };

  useEffect(() => {
    if (!invoicePendingDelete || deletingId) return undefined;
    const onKeyDown = (e) => {
      if (e.key === "Escape") setInvoicePendingDelete(null);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [invoicePendingDelete, deletingId]);

  useEffect(() => {
    if (!invoicePendingClose || closingAccount) return undefined;
    const onKeyDown = (e) => {
      if (e.key === "Escape") setInvoicePendingClose(null);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [invoicePendingClose, closingAccount]);

  useEffect(() => {
    if (!noteModalInvoice) return undefined;
    setNoteCopied(false);
    const onKeyDown = (e) => {
      if (e.key === "Escape") setNoteModalInvoice(null);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [noteModalInvoice]);

  const handleCopyInvoiceNote = useCallback(async () => {
    const body =
      noteContentRef.current?.innerText?.trim() ||
      String(noteModalInvoice?.note || "").trim();
    if (!body) {
      setToast({ message: "Nothing to copy to the clipboard", type: "error" });
      return;
    }
    const meta = [
      noteModalInvoice?.provider,
      noteModalInvoice?.billing_account,
      noteModalInvoice?.invoice_date
        ? formatInvoiceDate(noteModalInvoice.invoice_date)
        : null,
    ]
      .filter(Boolean)
      .join(" · ");
    const text = ["Invoice note", meta, "", body].filter(Boolean).join("\n");
    try {
      await navigator.clipboard.writeText(text);
      setNoteCopied(true);
      setToast({ message: "Note copied to clipboard", type: "success" });
      window.setTimeout(() => setNoteCopied(false), 2000);
    } catch {
      setToast({ message: "Failed to copy note to the clipboard", type: "error" });
    }
  }, [noteModalInvoice]);

  useEffect(() => {
    if (!emailConfirmRequest || emailBatchSending) return undefined;
    const onKeyDown = (e) => {
      if (e.key === "Escape") setEmailConfirmRequest(null);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [emailConfirmRequest, emailBatchSending]);

  useEffect(() => {
    if (!setHeaderOptionsContent) return;
    setHeaderOptionsContent(null);
    return () => setHeaderOptionsContent(null);
  }, [setHeaderOptionsContent]);

  const attentionCount =
    (statusCounts.missing || 0) +
    (statusCounts.needsReview || 0) +
    (statusCounts.missingGl || 0);
  const monthStepperLabel = selectedMonth
    ? formatMonthLongYm(selectedMonth)
    : "All invoices";
  const filterSelectClass =
    "h-[38px] w-full rounded-md border border-[var(--line-strong,#d5d3cc)] bg-white px-2.5 text-[13px] text-[var(--ink,#1c1c1a)] focus:outline-none focus:ring-2 focus:ring-[var(--muted,#6b6a66)]/20 focus:border-[var(--muted,#6b6a66)]";

  return (
    <div className="invoices-page min-h-screen pt-[80px] bg-[var(--ground,#f5f4f1)]">
      <div className="fixed top-4 right-4 z-[100]">
        <Toast
          message={toast.message}
          type={toast.type}
          title={toast.title}
          duration={
            toast.message && String(toast.message).includes("\n") ? 15000 : 8000
          }
          className={toast.message && String(toast.message).includes("\n") ? "max-w-xl" : ""}
          onClose={() => setToast({ message: null, type: "success" })}
        />
      </div>

      <div className="w-full px-5 py-2 space-y-4">
        {error && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-[10px] text-sm text-red-700">
            {error}
          </div>
        )}

        {/* Title strip */}
        <div className="flex flex-col xl:flex-row xl:items-end xl:justify-between gap-4">
          <div>
            <p className="text-[12px] font-semibold uppercase tracking-[0.05em] text-[var(--accent,#a51c1c)]">
              Invoices
            </p>
            <h1 className="mt-1 text-[26px] font-semibold leading-tight text-[var(--ink,#1c1c1a)]">
              Invoices
            </h1>
          </div>

          <div className="flex flex-wrap items-center gap-2.5">
            {/* Billing period + Focus/Compare */}
            <div className="inline-flex items-stretch rounded-[10px] border border-[var(--line,#e4e2dc)] bg-white shadow-sm overflow-hidden">
              <div className="px-3 py-2 border-r border-[var(--line,#e4e2dc)]">
                <div className="flex items-center justify-between gap-3">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.05em] text-[var(--muted,#6b6a66)]">
                    Billing period
                  </p>
                  {selectedMonth && (
              <button
                type="button"
                      onClick={() => {
                        setSelectedMonth("");
                        setStatusFilter("all");
                        setCurrentPage(1);
                      }}
                      className="text-[11px] font-medium text-[var(--muted,#6b6a66)] hover:text-[var(--accent,#a51c1c)] whitespace-nowrap"
                    >
                      All
              </button>
                  )}
              </div>
                <div className="mt-1 flex items-center gap-1.5">
                  <button
                    type="button"
                    onClick={() => handleMonthStep(-1)}
                    className="inline-flex h-7 w-7 items-center justify-center rounded-md text-[var(--muted,#6b6a66)] hover:bg-[var(--sunken,#eeece7)]"
                    aria-label="Previous month"
                  >
                    <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M12.79 5.23a.75.75 0 010 1.06L9.06 10l3.73 3.71a.75.75 0 11-1.06 1.06l-4.25-4.25a.75.75 0 010-1.06l4.25-4.25a.75.75 0 011.06 0z" clipRule="evenodd" />
                    </svg>
                  </button>
                  <div className="relative min-w-[132px]">
                    <div className="flex h-7 items-center justify-center gap-1.5 px-1 text-[13px] font-semibold text-[var(--ink,#1c1c1a)] tabular-nums">
                      <span className="truncate">{monthStepperLabel}</span>
                      <Calendar className="h-3.5 w-3.5 flex-shrink-0 text-[var(--faint,#9a9a95)]" strokeWidth={1.75} />
                    </div>
                    <input
                      ref={monthInputRef}
                      type="month"
                      value={selectedMonth}
                      onChange={(e) => {
                        setSelectedMonth(e.target.value);
                        setStatusFilter("all");
                        setCurrentPage(1);
                      }}
                      onClick={openMonthPicker}
                      className="absolute inset-0 z-10 h-full w-full cursor-pointer opacity-0"
                      aria-label="Invoice month"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => handleMonthStep(1)}
                    className="inline-flex h-7 w-7 items-center justify-center rounded-md text-[var(--muted,#6b6a66)] hover:bg-[var(--sunken,#eeece7)]"
                    aria-label="Next month"
                  >
                    <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M7.21 14.77a.75.75 0 010-1.06L10.94 10 7.21 6.29a.75.75 0 111.06-1.06l4.25 4.25a.75.75 0 010 1.06l-4.25 4.25a.75.75 0 01-1.06 0z" clipRule="evenodd" />
                    </svg>
                  </button>
                </div>
              </div>
              {selectedMonth ? (
                <div className="flex items-center gap-1 px-2">
                  <div className="inline-flex h-8 items-center rounded-md border border-[var(--line-strong,#d5d3cc)] overflow-hidden">
                <button
                  type="button"
                      onClick={() => setViewMode("focus")}
                      className={`px-3 h-full text-[12px] font-semibold transition-colors ${
                        viewMode === "focus"
                          ? "bg-[var(--accent,#a51c1c)] text-white"
                          : "bg-white text-[var(--muted,#6b6a66)] hover:bg-[var(--sunken,#eeece7)]"
                      }`}
                    >
                      Focus
                </button>
                    <button
                      type="button"
                      onClick={() => setViewMode("compare")}
                      className={`px-3 h-full text-[12px] font-semibold transition-colors ${
                        viewMode === "compare"
                          ? "bg-[var(--accent,#a51c1c)] text-white"
                          : "bg-white text-[var(--muted,#6b6a66)] hover:bg-[var(--sunken,#eeece7)]"
                      }`}
                    >
                      Compare
                    </button>
                  </div>
                </div>
              ) : null}
              </div>

            {userRole === "Admin" && typeof onAddProviderBillingAccount === "function" && (
              <button
                type="button"
                onClick={onAddProviderBillingAccount}
                className="inline-flex h-[38px] items-center rounded-md border border-[var(--accent,#a51c1c)] bg-white px-3 text-[13px] font-medium text-[var(--ink,#1c1c1a)] hover:bg-[var(--accent-soft,#fbe4e4)]"
              >
                Add billing account
              </button>
            )}

            <button
              type="button"
              onClick={() => setReportingOpen(true)}
              className="inline-flex h-[38px] items-center gap-2 rounded-md border border-[var(--accent,#a51c1c)] bg-white px-3 text-[13px] font-medium text-[var(--ink,#1c1c1a)] hover:bg-[var(--accent-soft,#fbe4e4)]"
            >
              <Calendar className="h-4 w-4" />
              Reporting months
            </button>

              <button
                type="button"
              onClick={() => setShowCreditLedgerModal(true)}
              className="inline-flex h-[38px] items-center gap-2 rounded-md border border-[var(--accent,#a51c1c)] bg-white px-3 text-[13px] font-medium text-[var(--ink,#1c1c1a)] hover:bg-[var(--accent-soft,#fbe4e4)]"
            >
              Credits
              {/* {statusCounts.credit > 0 && (
                <span className="inline-flex min-w-[18px] h-[18px] items-center justify-center rounded-full bg-[var(--info,#2c5fae)] px-1 text-[10px] font-bold text-white tabular-nums">
                  {statusCounts.credit}
                </span>
              )} */}
              </button>

                <button
                  type="button"
              onClick={exportCsv}
              disabled={!sortedInvoices.length}
              className="inline-flex h-[38px] items-center gap-2 rounded-md bg-[var(--accent,#a51c1c)] px-3.5 text-[13px] font-semibold text-white hover:bg-[#8c1a1a] disabled:opacity-40"
            >
              <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
              Export
                </button>
          </div>
        </div>

        {/* Summary tiles */}
                    {isComparisonView && progressSummary && comparisonSummary ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
            <div className="rounded-[10px] border border-[var(--line,#e4e2dc)] bg-white px-4 py-3">
              <p className="text-[11px] font-semibold uppercase tracking-[0.05em] text-[var(--muted,#6b6a66)]">
                {currentMonthShort || "Month"} total
              </p>
              <p className="mt-1 text-[22px] font-semibold tabular-nums text-[var(--ink,#1c1c1a)]">
                              {formatCurrency(comparisonSummary.currentTotal)}
                            </p>
              <p className="mt-1 text-[12px] text-[var(--muted,#6b6a66)]">
                {progressSummary.received} invoices received of {progressSummary.expected} active accounts
              </p>
                          </div>
            <div className="rounded-[10px] border border-[var(--line,#e4e2dc)] bg-white px-4 py-3">
              <p className="text-[11px] font-semibold uppercase tracking-[0.05em] text-[var(--muted,#6b6a66)]">
                Same accounts, {priorMonthShort || "prior"}
              </p>
              <p className="mt-1 text-[22px] font-semibold tabular-nums text-[var(--ink,#1c1c1a)]">
                              {formatCurrency(comparisonSummary.priorTotal)}
                            </p>
              <p className="mt-1 text-[12px] text-[var(--muted,#6b6a66)]">
                Prior total for the {progressSummary.received} received accounts
              </p>
                          </div>
            <div className="rounded-[10px] border border-[var(--line,#e4e2dc)] bg-white px-4 py-3">
              <p className="text-[11px] font-semibold uppercase tracking-[0.05em] text-[var(--muted,#6b6a66)]">
                Net change
              </p>
              <p
                className={`mt-1 text-[22px] font-semibold tabular-nums ${
                  (likeForLikeSummary?.change ?? comparisonSummary.momChange) > 0
                    ? "text-[var(--crit,#c0262b)]"
                    : (likeForLikeSummary?.change ?? comparisonSummary.momChange) < 0
                      ? "text-[var(--good,#1f6b34)]"
                      : "text-[var(--ink,#1c1c1a)]"
                }`}
              >
                {(likeForLikeSummary?.change ?? comparisonSummary.momChange) > 0 ? "+" : ""}
                {formatCurrency(likeForLikeSummary?.change ?? comparisonSummary.momChange)}
              </p>
              <p className="mt-1 text-[12px] text-[var(--muted,#6b6a66)]">
                {comparisonSummary.momPercent != null
                  ? `${comparisonSummary.momPercent > 0 ? "+" : ""}${comparisonSummary.momPercent}% vs ${priorMonthShort || "prior"}`
                  : `${likeForLikeSummary?.accountCount ?? 0} accounts in both months`}
                          </p>
                        </div>
            <div className="rounded-[10px] border border-[var(--line,#e4e2dc)] bg-white px-4 py-3">
              <p className="text-[11px] font-semibold uppercase tracking-[0.05em] text-[var(--muted,#6b6a66)]">
                Needs attention
              </p>
              <p className="mt-1 text-[22px] font-semibold tabular-nums text-[var(--crit,#c0262b)]">
                {attentionCount}
              </p>
              <p className="mt-1 text-[12px] text-[var(--muted,#6b6a66)]">
                {[
                  statusCounts.missing
                    ? `${statusCounts.missing} missing`
                    : null,
                  statusCounts.needsReview
                    ? `${statusCounts.needsReview} need review`
                    : null,
                  statusCounts.expected
                    ? `${statusCounts.expected} expected`
                    : null,
                  statusCounts.missingGl
                    ? `${statusCounts.missingGl} missing GL`
                    : null,
                ]
                  .filter(Boolean)
                  .join(" · ") || "No items need attention"}
                            </p>
                          </div>
                          </div>
        ) : filterTotals ? (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="rounded-[10px] border border-[var(--line,#e4e2dc)] bg-white px-4 py-3">
              <p className="text-[11px] font-semibold uppercase tracking-[0.05em] text-[var(--muted,#6b6a66)]">Total MRC</p>
              <p className="mt-1 text-[22px] font-semibold tabular-nums text-[var(--ink,#1c1c1a)]">{formatCurrency(filterTotals.mrc)}</p>
                          </div>
            <div className="rounded-[10px] border border-[var(--line,#e4e2dc)] bg-white px-4 py-3">
              <p className="text-[11px] font-semibold uppercase tracking-[0.05em] text-[var(--muted,#6b6a66)]">Total invoice amount</p>
              <p className="mt-1 text-[22px] font-semibold tabular-nums text-[var(--ink,#1c1c1a)]">{formatCurrency(filterTotals.total)}</p>
                        </div>
            <div className="rounded-[10px] border border-[var(--line,#e4e2dc)] bg-white px-4 py-3">
              <p className="text-[11px] font-semibold uppercase tracking-[0.05em] text-[var(--muted,#6b6a66)]">Total credits</p>
              <p className="mt-1 text-[22px] font-semibold tabular-nums text-[var(--ink,#1c1c1a)]">{formatCurrency(filterTotals.credit)}</p>
                  </div>
              </div>
        ) : null}

        {/* Status tabs */}
        {isComparisonView && (
          <p className="text-[12px] text-[var(--muted,#6b6a66)] leading-snug">
            <span className="font-medium text-[var(--ink,#1c1c1a)]">Status:</span>{" "}
            Expected (within grace) · Missing (past grace) · Needs review (MRC over threshold) ·
            Received (no further review) · New · Credit · Closed. Only Missing and Needs review use red.
          </p>
        )}
        <div className="border-b border-[var(--line,#e4e2dc)] overflow-x-auto overflow-y-hidden [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
          <div className="flex items-end gap-1 min-w-max">
  {statusFilterOptions.map((opt) => {
  const active = statusFilter === opt.id;
              const style = STATUS_FILTER_STYLES[opt.id] || STATUS_FILTER_STYLES.all;
  return (
    <button
      key={opt.id}
      type="button"
      onClick={() => {
        setStatusFilter(opt.id);
        setCurrentPage(1);
      }}
                  className={`inline-flex h-[38px] items-center gap-2 px-3 text-[13px] border-b-2 -mb-px transition-colors ${
          active
                      ? "border-[var(--accent,#a51c1c)] text-[var(--accent,#a51c1c)] font-semibold"
                      : "border-transparent text-[var(--muted,#6b6a66)] hover:text-[var(--ink,#1c1c1a)]"
                  }`}
                >
                  <span className={`h-1.5 w-1.5 rounded-full ${style.dot}`} />
                  {opt.label}
        <span
                    className={`inline-flex min-w-[20px] items-center justify-center rounded-full px-1.5 py-0.5 text-[11px] font-semibold tabular-nums ${
                      active
                        ? "bg-[var(--accent-soft,#fbe4e4)] text-[var(--accent,#a51c1c)]"
                        : "bg-[var(--sunken,#eeece7)] text-[var(--muted,#6b6a66)]"
                    }`}
                  >
          {opt.count ?? 0}
                  </span>
    </button>
  );
})}
  </div>
</div>

        {/* Filter bar */}
        <div className="rounded-[10px] border border-[var(--line,#e4e2dc)] bg-white px-4 py-3">
          <div className="flex flex-col xl:flex-row xl:items-end gap-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 flex-1 min-w-0">
              <div>
                <label className="block text-[11px] font-semibold uppercase tracking-[0.05em] text-[var(--muted,#6b6a66)] mb-1">
                  Search
                </label>
                <div className="relative">
                  <svg
                    className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--faint,#9a9a95)]"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <circle cx="11" cy="11" r="7" />
                    <path d="M21 21l-4.3-4.3" />
    </svg>
        <input
                    type="search"
                    value={searchQuery}
                    onChange={(e) => {
                      setSearchQuery(e.target.value);
                      setCurrentPage(1);
                    }}
                    placeholder="Account number or provider"
                    className={`${filterSelectClass} pl-8`}
                    aria-label="Search account number or provider"
                  />
    </div>
</div>
              <div>
                <label className="block text-[11px] font-semibold uppercase tracking-[0.05em] text-[var(--muted,#6b6a66)] mb-1">
                  Provider
                </label>
                <CustomSelect
                  value={selectedProvider}
                  onChange={(e) => setSelectedProvider(e.target.value)}
                  disabled={loadingProviders}
                  className={filterSelectClass}
                  searchable
                  searchPlaceholder="Type to filter providers…"
                >
                  <option value="">All</option>
                  {providers.map((p) => (
                    <option key={p} value={p}>{p}</option>
                  ))}
                </CustomSelect>
              </div>
              <div>
                <label className="block text-[11px] font-semibold uppercase tracking-[0.05em] text-[var(--muted,#6b6a66)] mb-1">
                  Billing account
                </label>
                <CustomSelect
                  value={selectedBillingAccount}
                  onChange={(e) => setSelectedBillingAccount(e.target.value)}
                  disabled={!selectedProvider || loadingAccounts}
                  className={filterSelectClass}
                  searchable
                  searchPlaceholder="Type to filter accounts…"
                >
                  <option value="">
                    {selectedProvider ? "All" : "Select provider first"}
                  </option>
                  {billingAccounts.map((acc) => {
                    const value = typeof acc === "string" ? acc : acc.billing_account;
                    return (
                      <option key={value} value={value}>{value}</option>
                    );
                  })}
                </CustomSelect>
  </div>
              <div>
                <label className="block text-[11px] font-semibold uppercase tracking-[0.05em] text-[var(--muted,#6b6a66)] mb-1">
                  GL code
                </label>
                <CustomSelect
                  value={selectedGlCode}
                  onChange={(e) => {
                    setSelectedGlCode(e.target.value);
                    setCurrentPage(1);
                  }}
                  className={filterSelectClass}
                >
                  <option value="">All</option>
                  {glFilterOptions.map((code) => (
                    <option key={code.gl_code} value={code.gl_code}>
                      {formatGlCodeLabel(code.gl_code, code.description)}
                    </option>
                  ))}
                </CustomSelect>
</div>
              <div>
                <label className="block text-[11px] font-semibold uppercase tracking-[0.05em] text-[var(--muted,#6b6a66)] mb-1">
                  AP status
                </label>
                <CustomSelect
                  value={apStatusSelectValue}
                  onChange={(e) => {
                    setStatusFilter(e.target.value);
                    setCurrentPage(1);
                  }}
                  className={filterSelectClass}
                >
                  {apStatusFilterOptions.map((opt) => (
                    <option key={opt.id} value={opt.id}>{opt.label}</option>
                  ))}
                </CustomSelect>
              </div>
    </div>

            <div className="flex flex-wrap items-center gap-3 xl:justify-end">
              <label className="inline-flex items-center gap-1.5 text-[12px] text-[var(--muted,#6b6a66)] cursor-pointer">
        <input
          type="checkbox"
          checked={showCreditColumn}
          onChange={(e) => setShowCreditColumn(e.target.checked)}
                  className="h-3.5 w-3.5 rounded border-[var(--line-strong,#d5d3cc)] text-[var(--accent,#a51c1c)] focus:ring-[var(--accent,#a51c1c)]"
        />
                Credits col.
      </label>
              <label className="inline-flex items-center gap-1.5 text-[12px] text-[var(--muted,#6b6a66)] cursor-pointer">
        <input
          type="checkbox"
          checked={showInvoiceNumberColumn}
          onChange={(e) => setShowInvoiceNumberColumn(e.target.checked)}
                  className="h-3.5 w-3.5 rounded border-[var(--line-strong,#d5d3cc)] text-[var(--accent,#a51c1c)] focus:ring-[var(--accent,#a51c1c)]"
        />
        Invoice No.
      </label>
              <button
                type="button"
                aria-pressed={hideClosedAccounts}
                onClick={() => {
                  setHideClosedAccounts((v) => !v);
                  setCurrentPage(1);
                }}
                className={`inline-flex h-[38px] items-center rounded-md border px-3 text-[13px] font-medium ${
                  hideClosedAccounts
                    ? "border-[var(--accent,#a51c1c)] bg-[var(--accent-soft,#f3e8e8)] text-[var(--accent,#a51c1c)]"
                    : "border-[var(--line-strong,#d5d3cc)] bg-white text-[var(--ink,#1c1c1a)] hover:bg-[var(--sunken,#eeece7)]"
                }`}
              >
                Hide closed accounts
              </button>
  <button
    type="button"
                onClick={handleClearAllFilters}
                className="inline-flex h-[38px] items-center rounded-md border border-[var(--line-strong,#d5d3cc)] bg-white px-3 text-[13px] font-medium text-[var(--ink,#1c1c1a)] hover:bg-[var(--sunken,#eeece7)]"
              >
                Reset
  </button>
              <p className="text-[12px] text-[var(--muted,#6b6a66)] whitespace-nowrap">
                Showing {sortedInvoices.length} of {searchFilteredInvoices.length} accounts
              </p>
    </div>
  </div>
</div>

        {/* Table card */}
        <div className="rounded-[10px] border border-[var(--line,#e4e2dc)] bg-white overflow-hidden min-w-0">

          {canSendEmail && selectedEmailIds.size > 0 && (
            <div className="flex items-center justify-between gap-3 border-b border-sky-100 bg-sky-50/90 px-5 py-3">
              <p className="text-sm font-medium text-sky-950">
                {selectedEmailIds.size} invoice{selectedEmailIds.size === 1 ? "" : "s"} selected
              </p>
              <button
                type="button"
                onClick={handleSendSelectedEmails}
                disabled={emailBatchSending || sendingEmailId != null}
                className="inline-flex items-center gap-2 rounded-lg bg-sky-700 px-4 py-2 text-sm font-semibold text-white hover:bg-sky-800 disabled:opacity-50"
              >
                {emailBatchSending ? "Sending…" : "Send selected to AP"}
                <svg className="w-4 h-4" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                  <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                </svg>
              </button>
            </div>
          )}

          <div className="p-0">
            {loadingInvoices ? (
              <div className="flex items-center justify-center py-12">
                <SpinnerCustom />
              </div>
            ) : invoices.length === 0 ? (
              <p className="text-sm text-stone-500 py-8 px-5">
                {selectedMonth
                  ? "No invoices match this month and filters."
                  : "No invoices in the last 18 months for the selected filters."}
              </p>
            ) : sortedInvoices.length === 0 ? (
              <p className="text-sm text-stone-500 py-8 px-5">
                No invoices match the current status filter.
              </p>
            ) : (
              <>
                <div className="overflow-x-hidden">
                  {isComparisonView ? (
                    <InvoicesMonthComparisonTable
                      viewMode={viewMode}
                      comparison={comparison}
                      tableTotals={comparisonTableTotals}
                      rows={paginatedInvoices}
                      sortConfig={sortConfig}
                      onSort={handleSort}
                      editingInvoiceId={editingInvoiceId}
                      editInvoiceDate={editInvoiceDate}
                      setEditInvoiceDate={setEditInvoiceDate}
                      editInvoiceNumber={editInvoiceNumber}
                      setEditInvoiceNumber={setEditInvoiceNumber}
                      editGlCode={editGlCode}
                      setEditGlCode={setEditGlCode}
                      editGlAllocations={editGlAllocations}
                      setEditGlAllocations={setEditGlAllocations}
                      editGlSplit={editGlSplit}
                      setEditGlSplit={setEditGlSplit}
                      glCodes={glCodes}
                      editMonthlyMrc={editMonthlyMrc}
                      setEditMonthlyMrc={setEditMonthlyMrc}
                      editTotalAmount={editTotalAmount}
                      setEditTotalAmount={setEditTotalAmount}
                      editCredit={editCredit}
                      setEditCredit={setEditCredit}
                      editTaxesAndSurcharges={editTaxesAndSurcharges}
                      setEditTaxesAndSurcharges={setEditTaxesAndSurcharges}
                      editDueDate={editDueDate}
                      setEditDueDate={setEditDueDate}
                      editNote={editNote}
                      setEditNote={setEditNote}
                      savingInvoiceId={savingInvoiceId}
                      generatingNoteId={generatingNoteId}
                      renderChangeCell={renderChangeCell}
                      renderNoteCell={renderNoteCell}
                      noteAutomationSettings={noteAutomationSettings}
                      canEditInvoice={canEditInvoice}
                      canDeleteInvoice={canDeleteInvoice}
                      canCloseAccount={canCloseAccount}
                      canAddInvoice={canAddInvoice}
                      onAddMissing={handleAddMissingInvoice}
                      onDownload={handleDownload}
                      downloadingId={downloadingId}
                      deletingId={deletingId}
                      onSaveEdit={handleSaveInvoiceEdit}
                      onCancelEdit={cancelEditInvoice}
                      onGenerateNote={handleGenerateNote}
                      canShowGenerateNote={canShowGenerateNote}
                      onMarkReviewed={handleMarkReviewed}
                      canShowMarkReviewed={canShowMarkReviewed}
                      onOpenDelete={handleOpenDeleteModal}
                      onOpenClose={handleOpenCloseModal}
                      beginEditInvoice={beginEditInvoice}
                      canSendEmail={canSendEmail}
                      selectedEmailIds={selectedEmailIds}
                      onToggleEmailSelect={handleToggleEmailSelect}
                      onSendEmail={requestSendEmail}
                      onResendEmail={requestResendEmail}
                      sendingEmailId={sendingEmailId || (emailBatchSending ? -1 : null)}
                      formatEmailSentDate={formatEmailSentDate}
                      onToggleSelectAll={handleToggleSelectAllOnPage}
                      showCreditColumn={showCreditColumn}
                      showInvoiceNumberColumn={showInvoiceNumberColumn}
                      onViewCircuits={handleViewCircuitsOnAccount}
                    />
                  ) : (
                      <InvoicesAllTimeTable
    rows={paginatedInvoices}
    sortConfig={sortConfig}
    onSort={handleSort}
    editingInvoiceId={editingInvoiceId}
    editInvoiceDate={editInvoiceDate}
    setEditInvoiceDate={setEditInvoiceDate}
    editInvoiceNumber={editInvoiceNumber}
    setEditInvoiceNumber={setEditInvoiceNumber}
    editGlCode={editGlCode}
    setEditGlCode={setEditGlCode}
    editGlAllocations={editGlAllocations}
    setEditGlAllocations={setEditGlAllocations}
    editGlSplit={editGlSplit}
    setEditGlSplit={setEditGlSplit}
    glCodes={glCodes}
    editMonthlyMrc={editMonthlyMrc}
    setEditMonthlyMrc={setEditMonthlyMrc}
    editTotalAmount={editTotalAmount}
    setEditTotalAmount={setEditTotalAmount}
    editCredit={editCredit}
    setEditCredit={setEditCredit}
    editTaxesAndSurcharges={editTaxesAndSurcharges}
    setEditTaxesAndSurcharges={setEditTaxesAndSurcharges}
    editDueDate={editDueDate}
    setEditDueDate={setEditDueDate}
    editNote={editNote}
    setEditNote={setEditNote}
    savingInvoiceId={savingInvoiceId}
    generatingNoteId={generatingNoteId}
    renderNoteCell={renderNoteCell}
    canEditInvoice={canEditInvoice}
    canDeleteInvoice={canDeleteInvoice}
    canCloseAccount={canCloseAccount}
    onDownload={handleDownload}
    downloadingId={downloadingId}
    deletingId={deletingId}
    onSaveEdit={handleSaveInvoiceEdit}
    onCancelEdit={cancelEditInvoice}
    onGenerateNote={handleGenerateNote}
    canShowGenerateNote={canShowGenerateNote}
    onMarkReviewed={handleMarkReviewed}
    canShowMarkReviewed={canShowMarkReviewed}
    onOpenDelete={handleOpenDeleteModal}
    onOpenClose={handleOpenCloseModal}
    beginEditInvoice={beginEditInvoice}
    canSendEmail={canSendEmail}
    selectedEmailIds={selectedEmailIds}
    onToggleEmailSelect={handleToggleEmailSelect}
    onSendEmail={requestSendEmail}
    onResendEmail={requestResendEmail}
    sendingEmailId={sendingEmailId || (emailBatchSending ? -1 : null)}
    formatEmailSentDate={formatEmailSentDate}
    onToggleSelectAll={handleToggleSelectAllOnPage}
    tableTotals={filterTotals ? { ...filterTotals, count: sortedInvoices.length } : null}
    showCreditColumn={showCreditColumn}
    showInvoiceNumberColumn={showInvoiceNumberColumn}
    noteAutomationSettings={noteAutomationSettings}
    onViewCircuits={handleViewCircuitsOnAccount}
  />
                    )}
                </div>
                <div className="px-4 pb-4">
                  <InvoicesTablePagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    itemsPerPage={itemsPerPage}
                    totalItems={sortedInvoices.length}
                    onPageChange={setCurrentPage}
                    onPageSizeChange={(size) => {
                      setItemsPerPage(size);
                      setCurrentPage(1);
                    }}
                  />
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      <InvoiceEmailSendStatusModal
        status={emailSendStatus}
        onClose={() => {
          if (emailSendStatus?.phase === "sending") return;
          setEmailSendStatus(null);
        }}
        onConfirmMissingGl={handleConfirmMissingGlSend}
      />

      <InvoiceCreditLedgerModal
        open={showCreditLedgerModal}
        onClose={() => setShowCreditLedgerModal(false)}
        canMutate={canEditInvoice}
        onDownloadInvoice={handleDownload}
        downloadingId={downloadingId}
        onLedgerChange={refreshInvoices}
      />

      <ReportingMonthsModal
        open={reportingOpen}
        onClose={() => setReportingOpen(false)}
        tenantId={tenantId}
        canEdit={canMutateTenantData(userRole)}
        initialYear={
          Number(String(selectedMonth || "").slice(0, 4)) || undefined
        }
        setToast={setToast}
      />

      {emailConfirmRequest && (() => {
        const inv = invoiceById.get(emailConfirmRequest.invoiceId);
        const isResend = emailConfirmRequest.mode === "resend";
        return (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100] p-4"
            role="presentation"
            onClick={handleCancelEmailConfirm}
          >
            <div
              role="dialog"
              aria-modal="true"
              aria-labelledby="email-confirm-title"
              className="bg-white rounded-lg max-w-md w-full shadow-xl border-t-4 border-[var(--accent,#a51c1c)] overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="px-5 pt-4 pb-2 border-b border-gray-100 bg-[var(--accent,#a51c1c)]/5">
                <h2 id="email-confirm-title" className="text-lg font-bold text-gray-900">
                  {isResend ? "Resend invoice to AP?" : "Send invoice to AP?"}
                </h2>
                <p className="text-sm text-gray-600 mt-1">
                  {isResend
                    ? "This will resend the invoice email to accounts payable."
                    : "This will email the invoice to accounts payable."}
                </p>
              </div>
              {inv && (
                <div className="px-5 py-4">
                  <div className="bg-gray-50 rounded-md border border-gray-200 p-3 space-y-2 text-sm">
                    <div>
                      <span className="font-semibold text-gray-700">Provider: </span>
                      <span className="text-gray-800">{inv.provider}</span>
                    </div>
                    <div>
                      <span className="font-semibold text-gray-700">Billing account: </span>
                      <span className="text-gray-800 tabular-nums">{inv.billing_account}</span>
                    </div>
                    <div>
                      <span className="font-semibold text-gray-700">Total: </span>
                      <span className="text-gray-800 tabular-nums">
                        {formatCurrency(inv.total_invoice_amount ?? 0)}
                      </span>
                    </div>
                  </div>
                </div>
              )}
              <div className="px-5 pb-5 flex gap-2 justify-end border-t border-gray-100 pt-4">
                <button
                  type="button"
                  onClick={handleCancelEmailConfirm}
                  disabled={emailBatchSending}
                  className="inline-flex items-center justify-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-400 disabled:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleConfirmEmailSend}
                  disabled={emailBatchSending}
                  className="inline-flex items-center justify-center px-4 py-2 border-0 text-sm font-medium rounded-md text-white bg-[var(--accent,#a51c1c)] hover:bg-[#8c1a1a] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[var(--accent,#a51c1c)] disabled:bg-gray-400 disabled:cursor-not-allowed disabled:opacity-50 transition-colors"
                >
                  {isResend ? "Resend email" : "Send email"}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {noteModalInvoice && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100] p-4"
          role="presentation"
          onClick={() => setNoteModalInvoice(null)}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="invoice-note-title"
            className="bg-white rounded-lg max-w-lg w-full max-h-[85vh] shadow-xl border border-gray-200 flex flex-col overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-5 pt-4 pb-2 border-b border-gray-100 flex-shrink-0 flex items-start justify-between gap-3">
              <div className="min-w-0">
                <h2
                  id="invoice-note-title"
                  className="text-lg font-semibold text-gray-900"
                >
                  Invoice note
                </h2>
                <p className="text-xs text-gray-500 mt-1">
                  {noteModalInvoice.provider} · {noteModalInvoice.billing_account} ·{" "}
                  {formatInvoiceDate(noteModalInvoice.invoice_date)}
                </p>
              </div>
              <button
                type="button"
                onClick={handleCopyInvoiceNote}
                className="inline-flex items-center gap-1.5 flex-shrink-0 px-3 py-1.5 border border-gray-300 text-xs font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-blue-500"
                title="Copy note to clipboard"
              >
                {noteCopied ? (
                  <>
                    <Check className="h-3.5 w-3.5 text-emerald-600" aria-hidden />
                    Copied
                  </>
                ) : (
                  <>
                    <Copy className="h-3.5 w-3.5" aria-hidden />
                    Copy
                  </>
                )}
              </button>
            </div>
            <div
              ref={noteContentRef}
              className="px-5 py-4 overflow-y-auto flex-1 min-h-0 text-sm text-gray-700"
            >
              <ReactMarkdown components={noteMarkdownComponents}>
                {noteModalInvoice.note || ""}
              </ReactMarkdown>
            </div>
            <div className="px-5 pb-4 pt-2 border-t border-gray-100 flex justify-end flex-shrink-0">
              <button
                type="button"
                onClick={() => setNoteModalInvoice(null)}
                className="inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-[var(--accent,#a51c1c)] hover:bg-[#8c1a1a] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[var(--accent,#a51c1c)]"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {canDeleteInvoice && invoicePendingDelete && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100] p-4"
          role="presentation"
          onClick={handleCancelDeleteInvoice}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="invoice-delete-title"
            className="bg-white rounded-lg max-w-md w-full shadow-xl border-t-4 border-brand-red overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-5 pt-4 pb-2 border-b border-gray-100 bg-primary-50/80">
              <h2
                id="invoice-delete-title"
                className="text-lg font-bold text-brand-red"
              >
                Delete invoice
              </h2>
              <p className="text-sm text-gray-600 mt-1">
                This removes the invoice record and its PDF. You cannot undo this.
              </p>
            </div>
            <div className="px-5 py-4">
              <div className="bg-gray-50 rounded-md border border-gray-200 p-3 space-y-2 text-sm">
                <div>
                  <span className="font-semibold text-gray-700">Invoice month: </span>
                  <span className="text-gray-800">{invoicePendingDelete.invoice_date}</span>
                </div>
                <div>
                  <span className="font-semibold text-gray-700">Provider: </span>
                  <span className="text-gray-800">{invoicePendingDelete.provider}</span>
                </div>
                <div>
                  <span className="font-semibold text-gray-700">Billing account: </span>
                  <span className="text-gray-800 tabular-nums">{invoicePendingDelete.billing_account}</span>
                </div>
                <div>
                  <span className="font-semibold text-gray-700">Total: </span>
                  <span className="text-gray-800 tabular-nums">
                    {formatCurrency(invoicePendingDelete.total_invoice_amount ?? 0)}
                  </span>
                </div>
              </div>
            </div>
            <div className="px-5 pb-5 flex gap-2 justify-end border-t border-gray-100 pt-4">
              <button
                type="button"
                onClick={handleCancelDeleteInvoice}
                disabled={!!deletingId}
                className="inline-flex items-center justify-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-400 disabled:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50 transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmDeleteInvoice}
                disabled={!!deletingId}
                className="inline-flex items-center justify-center px-4 py-2 border-0 text-sm font-medium rounded-md text-white bg-brand-red hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-red disabled:bg-gray-400 disabled:cursor-not-allowed disabled:opacity-50 transition-colors"
              >
                {deletingId === invoicePendingDelete.id ? "Deleting..." : "Delete invoice"}
              </button>
            </div>
          </div>
        </div>
      )}

      {canCloseAccount && invoicePendingClose && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100] p-4"
          role="presentation"
          onClick={handleCancelCloseAccount}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="invoice-close-account-title"
            className="bg-white rounded-lg max-w-md w-full shadow-xl border-t-4 border-stone-700 overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-5 pt-4 pb-2 border-b border-gray-100 bg-stone-50">
              <h2
                id="invoice-close-account-title"
                className="text-lg font-bold text-stone-900"
              >
                Close account
              </h2>
              <p className="text-sm text-gray-600 mt-1">
                This sets the account to Closed in tenant settings. This month and prior months keep their records. Future months will not list or count this account.
              </p>
            </div>
            <div className="px-5 py-4">
              <div className="bg-gray-50 rounded-md border border-gray-200 p-3 space-y-2 text-sm">
                <div>
                  <span className="font-semibold text-gray-700">Provider: </span>
                  <span className="text-gray-800">{invoicePendingClose.provider}</span>
                </div>
                <div>
                  <span className="font-semibold text-gray-700">Billing account: </span>
                  <span className="text-gray-800">{invoicePendingClose.billing_account}</span>
                </div>
              </div>
            </div>
            <div className="px-5 pb-5 flex gap-2 justify-end border-t border-gray-100 pt-4">
              <button
                type="button"
                onClick={handleCancelCloseAccount}
                disabled={closingAccount}
                className="inline-flex items-center justify-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-400 disabled:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50 transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmCloseAccount}
                disabled={closingAccount}
                className="inline-flex items-center justify-center px-4 py-2 border-0 text-sm font-medium rounded-md text-white bg-stone-800 hover:bg-stone-900 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-stone-700 disabled:bg-gray-400 disabled:cursor-not-allowed disabled:opacity-50 transition-colors"
              >
                {closingAccount ? "Closing..." : "Close account"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InvoicesPage;
